import {Option} from "../components/Form/Form/Control";
import ngage from "../services/ngage";
import {Exhibit, ExhibitPreview} from "../types/Exhibit";
import {Site, SiteListing} from "../types/Site";
import SponsorableType from "../types/SponsorableType";
import IconName from "../types/IconName";
import {Zone, ZoneListing} from "../types/Zone";
import {Facility, FacilityPreview} from "../types/Facility";
import {Element, ElementPreview} from "../types/Element";
import {useQuery} from "react-query";
import {Sponsorable} from "../types/Sponsorship";
import {Trail, TrailPreview} from "../types/Trail";
import {toTitleCase} from "../services/string";

// Helpers ----

function siteAsOption(site: Site | SiteListing): Option {
  return {
    type: SponsorableType.SITE,
    id: site.id,
    name: site.name,
    description: 'Site',
    icon: IconName.OFFICE_BUILDING
  }
}

function zoneAsOption(zone: Zone | ZoneListing): Option {
  return {
    type: SponsorableType.ZONE,
    id: zone.id,
    name: zone.name,
    description: 'Zone',
    icon: IconName.CUBE_TRANSPARENT
  }
}

function facilityAsOption(facility: Facility | FacilityPreview): Option {
  return {
    type: SponsorableType.FACILITY,
    id: facility.id,
    name: facility.name,
    description: `Facility ${facility.type?.name ? `(${facility.type.name})` : ''}`,
    icon: IconName.OFFICE_BUILDING
  }
}

function exhibitAsOption(exhibit: Exhibit | ExhibitPreview): Option {
  return {
    type: SponsorableType.EXHIBIT,
    id: exhibit.id,
    name: exhibit.name,
    description: `Exhibit (${exhibit.type?.name})`,
    icon: IconName.LIBRARY
  }
}

function elementAsOption(element: Element | ElementPreview): Option {
  return {
    type: SponsorableType.ELEMENT,
    id: element.id,
    name: element.name,
    description: 'Element',
    icon: IconName.PUZZLE,
    indent: true
  }
}

function trailAsOption(trail: Trail | TrailPreview): Option {
  return {
    type: SponsorableType.TRAIL,
    id: trail.id,
    name: trail.name,
    description: `Trail (${toTitleCase(trail.type)})`,
    icon: IconName.MAP,
  }
}

async function fetchSponsorables(search?: string | null): Promise<Array<Option>> {
  const sponsorables = await Promise.all([
    ngage.sites.filtered({search}).then(page => page?.data || []),
    ngage.zones.filtered({search}).then(page => page?.data || []),
    ngage.facilities.filtered({search}).then(page => page?.data || []),
    ngage.exhibits.filtered({published: true, search}).then(page => page?.data || []),
    ngage.elements.all({search}).then(page => page?.data || []),
    ngage.trails.filtered({search}).then(page => page?.data || []),
  ])

  const options: Array<Option> = []

  options.push(...sponsorables[0].map(siteAsOption))
  options.push(...sponsorables[1].map(zoneAsOption))
  options.push(...sponsorables[2].map(facilityAsOption))
  options.push(...sponsorables[5].map(trailAsOption))

  // Add any missing exhibits for matching elements
  sponsorables[4].forEach(element => {
    if (!sponsorables[3].find(exhibit => exhibit.id === element.exhibit_id)) {
      if (element.exhibit) {
        sponsorables[3].push(element.exhibit as Exhibit)
      }
    }
  })

  sponsorables[3].forEach(exhibit => {
    options.push(exhibitAsOption(exhibit))

    sponsorables[4].forEach(element => {
      if (element.exhibit_id === exhibit.id) {
        options.push(elementAsOption(element))
      }
    })
  })

  return options
}

export function sponsorableAsOption(sponsorable: Sponsorable, type: SponsorableType): Option {
  switch (type) {
    case ('site'):
      return siteAsOption(sponsorable as SiteListing)
    case ('zone'):
      return zoneAsOption(sponsorable as ZoneListing)
    case ('exhibit'):
      return exhibitAsOption(sponsorable as ExhibitPreview)
    case ('element'):
      return elementAsOption(sponsorable as ElementPreview)
    case ('trail'):
      return trailAsOption(sponsorable as TrailPreview)
    default:
      return facilityAsOption(sponsorable as FacilityPreview)
  }
}

// Hook ----

export default function useSponsorableOptions(searchTerm?: string | null) {
  return useQuery(
    ['sponsorables', {searchTerm}],
    () => fetchSponsorables(searchTerm),
    {keepPreviousData: true}
  )
}